<template>
  <div
    v-if="modelValue && videoId"
    class="video-stream"
    :style="style"
    :class="{ '-loading': !loaded }"
    ref="root"
  >
    <transition name="fade">
      <iframe
        v-if="scriptLoaded"
        :src="src"
        allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
        allowfullscreen="true"
        preload="none"
        title="video player"
        style="width: 100%; height: 100%; border-style: none"
        @error="error"
        @load="loaded = true"
        ref="player"
        :id="elementId"
      />
    </transition>
  </div>
</template>
<script>

const scriptLocation = 'https://embed.videodelivery.net/embed/sdk.latest.js';
export default {
  name: 'VideoStream',
  components: {
  },
  props: {
    modelValue: Object,
    preview: Boolean,
    previewHeight: Number,
    fillHeight: Boolean,
    settings: Object,
    orientation: String,
    fullWidth: {
      type: Boolean,
      default: true
    },
    mouseOver: Boolean,
    preventOverflowHeight: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      scriptLoaded: false,
      mounted: false,
      hasInit: false,
      loaded: false,
      totalWidth: 0,
      streamScript: null,
      streamPlayer: null,
      playerReady: false,
      loadEvent: false,
      durationEvents: [
        {
          value: 0.1,
          triggered: false,
          label: 'impression10'
        },
        {
          value: 0.5,
          triggered: false,
          label: 'impression50'
        },
        {
          value: 0.9,
          triggered: false,
          label: 'impression90'
        }
      ]

    };
  },
  watch: {
    modelValue: {
      deep: true,
      handler (newval, oldval) {
        if (newval !== oldval && this.streamScript) {
          this.$nextTick(() => {
            if (typeof Stream === 'function' && this.scriptLoaded) {
              this.initPlayer();
            }
          });
        }
      }
    },
    preview (val) {
      if (this.streamPlayer) {
        this.togglePreview(val);
      }
    },
    mouseOver (val) {
      if (this.streamPlayer && this.settings.mouseTrigger && this.preview) {
        if (val) {
          this.streamPlayer.play();
        } else {
          this.streamPlayer.pause();
        }
      }
    }
  },
  methods: {
    togglePreview (val) {
      if (val) {
        // preview
        this.streamPlayer.currentTime = this.settings.loopStart;
        this.streamPlayer.controls = false;
        this.streamPlayer.muted = true;
      } else {
        // full movie
        this.streamPlayer.currentTime = 0;
        this.streamPlayer.controls = !this.settings.hideControls;
        this.streamPlayer.muted = !!this.settings.muted;
        if (!this.settings.noPreload) {
          this.streamPlayer.play();
        }
      }
    },
    initPlayer () {
      if (!this.hasInit) {
        this.$emit('on-init');

        this.hasInit = true;
        // eslint-disable-next-line no-undef
        this.streamPlayer = Stream(document.querySelector(`iframe[id="${this.elementId}"]`));
        this.togglePreview(this.preview);
        this.streamPlayer.addEventListener('loadeddata', () => {
          if (!this.loadEvent) {
            // console.log('loadeddata');
            this.loadEvent = true;
            this.$emit('on-load', this.streamPlayer);
          }
        });
        if (this.streamPlayer) {
          this.addObserver();
          this.streamPlayer.addEventListener('seeked', () => {
          // console.log('seeked: ', this.streamPlayer.currentTime);
            this.$emit('on-seeked');
          });
          if (!this.settings.mouseTrigger && !this.settings.previewPoster) {
            this.streamPlayer.addEventListener('canplay', () => {
              // console.log('canplay');
              if (!this.settings.noPreload) {
                this.streamPlayer.play();
              }
            });
          }

          if (this.settings.preview) {
            this.streamPlayer.currentTime = this.settings.loopStart;
          }
          this.streamPlayer.addEventListener('timeupdate', () => {
            this.timeEvents();
            if (this.settings.preview) {
              if (this.preview && this.streamPlayer && this.streamPlayer.currentTime >= this.settings.loopEnd) {
                if (this.settings.previewLoop) {
                  this.streamPlayer.currentTime = this.settings.loopStart;
                } else {
                  this.streamPlayer.pause();
                }
              }
            }
          });
        }
        this.$emit('on-init');
      }
    },
    addStreamScript () {
      this.streamScript = this.streamScript || document.querySelector(`script[src="${scriptLocation}"]`);
      if (!this.streamScript) {
        const streamScript = document.createElement('script');
        streamScript.setAttribute('data-cfasync', 'false');
        streamScript.setAttribute('defer', 'true');
        streamScript.setAttribute('type', 'text/javascript');
        streamScript.setAttribute('src', scriptLocation);
        document.head.appendChild(streamScript);
        this.streamScript = streamScript;
      }
      if (this.streamScript.dataset.loaded === 'true') {
        this.scriptLoaded = true;
        this.$nextTick(() => {
          this.initPlayer();
        });
      } else {
        this.streamScript.addEventListener('load', () => {
          this.scriptLoaded = true;
          this.streamScript.setAttribute('data-loaded', true);
          this.$nextTick(() => {
            this.initPlayer();
          });
        });
        // this.$nextTick(() => {
        //   if (typeof Stream === 'function') {
        //     this.initPlayer();
        //   }
        // });
      }
    },
    error (err) {
      console.log(err);
    },
    setWidth () {
      this.totalWidth = document.documentElement.clientWidth || document.body.clientWidth;
    },
    timeEvents () {
      this.durationEvents.forEach((event) => {
        if (!event.triggered && this.streamPlayer.currentTime > this.streamPlayer.duration * event.value) {
          event.triggered = true;
          this.$emit('on-duration', event.label);
        }
      });
    },
    addObserver () {
      const observer = new window.IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          if (!this.settings.mouseTrigger && this.streamPlayer) {
            this.streamPlayer.play();
          }
          return;
        }
        // this.isVisible = false;
        if (this.streamPlayer) {
          this.streamPlayer.pause();
        }
      }, {
        root: null,
        threshold: 0.3 // set offset 0.1 means trigger if atleast 10% of element in viewport
      });
      observer.observe(this.$refs.root);
    }
  },
  computed: {

    isMobile () {
      return this.$mqAliases.xs || this.$mqAliases.sm;
    },
    proportion () {
      if (this.modelValue.Width === 0 || this.modelValue.Height === 0) {
        return 16 / 9;
      }
      return this.modelValue.Width / this.modelValue.Height;
    },
    src () {
      const path = 'https://iframe.videodelivery.net';
      const muted = true;
      const autoplay = !this.settings.noPreload && !this.settings.mouseTrigger && !this.settings.previewPoster ? 'autoplay=true' : '';
      const noPreload = this.settings.noPreload ? '&preload=false' : '';
      const letterBox = this.settings.transparent ? '&letterboxColor=transparent' : '';
      return `${path}/${this.videoId}?startTime=${this.settings.loopStart}&${autoplay}${letterBox}&muted=${muted}${noPreload}&playsinline=true&loop=true&controls=false&poster=${this.poster}`;
    },
    poster () {
      return encodeURIComponent(`https://videodelivery.net/${this.videoId}/thumbnails/thumbnail.jpg?time=${this.settings.loopStart}s`);
    },
    elementId () {
      return `${this.videoId}-${Math.random() * 10000}`;
    },
    videoId () {
      if (!this.modelValue) {
        return null;
      }
      return this.modelValue.PathCdn.replace('?tusv2=true', '');
    },
    height () {
      return (this.totalWidth / this.proportion);
    },
    style () {
      // mobile preview overflow
      if (this.preview && this.fillHeight && this.previewHeight) {
        return {
          overflow: 'hidden',
          position: 'absolute',
          top: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          minWidth: `${Math.ceil(this.previewHeight * this.proportion)}px`

        };
      }
      if (this.mounted && this.totalWidth && this.proportion > 0) {
        if (this.preventOverflowHeight && !this.preview && this.height > window.innerHeight) {
          // landscape issue
          return {
            width: '100%',
            height: 'calc(100vh)',
            alignSelf: 'flex-start'
          };
        }
        const height = `${this.height}px`;
        return {
          overflow: 'hidden',
          height: height
        };
      }
      return {};
    }

  },
  created () {
    this.addStreamScript();
  },
  mounted () {
    this.$nextTick(() => {
      this.mounted = true;
      this.$emit('ready');
      if (this.fullWidth) {
        this.setWidth();
        window.addEventListener('resize', () => {
          this.setWidth();
        });
      }
    });
  },
  destroyed () {
    this.streamScript = null;
    this.streamPlayer = null;
  }
};
</script>
<style lang="scss">
.video-stream {
  width: 100%;
  height: 100%;
}
</style>
